import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  UseSpeechRecognitionProps,
  UseSpeechRecognitionReturn,
  SpeechRecognitionStatus,
  SpeechRecognition,
} from './types';

// Base hook - pure Web Speech API integration
export const useSpeechRecognition = ({
  continuous = true,
  language = 'en-US',
  interimResults = true,
  onResult,
  onError,
}: UseSpeechRecognitionProps = {}): UseSpeechRecognitionReturn => {
  const [status, setStatus] = useState<SpeechRecognitionStatus>('inactive');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(false);

  const hasRecognitionSupport =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const initializeRecognition = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not necessary, firefox does not have it
    const SpeechRecognitionImpl = window.SpeechRecognition || window.webkitSpeechRecognition;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not necessary, firefox does not have it
    if (!SpeechRecognitionImpl) {
      setError('Speech recognition is not supported');
      return null;
    }

    const recognition = new SpeechRecognitionImpl();

    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;

    recognition.onstart = () => {
      setStatus('listening');
      isListeningRef.current = true;
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        if (continuous) {
          recognition.start();
        } else {
          setStatus('inactive');
          isListeningRef.current = false;
        }
      }
    };

    recognition.onerror = (event) => {
      if (['not-allowed', 'service-not-allowed', 'audio-capture'].includes(event.error)) {
        recognition.stop();
      }

      const errorMessage = event.error;
      setError(errorMessage);
      setStatus('error');
      onError?.(errorMessage);
      isListeningRef.current = false;
    };

    recognition.onresult = (event) => {
      onResult?.(event);
    };

    return recognition;
  }, [continuous, interimResults, language, onError, onResult]);

  useEffect(() => {
    if (hasRecognitionSupport) {
      recognitionRef.current = initializeRecognition();
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();

        // Remove all event listeners to prevent memory leaks
        recognitionRef.current.onstart = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
      }
    };
  }, [initializeRecognition, hasRecognitionSupport]);

  const startListening = useCallback(() => {
    setError(null);

    if (!recognitionRef.current) {
      recognitionRef.current = initializeRecognition();
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        isListeningRef.current = true;
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
          onError?.(err.message);
        }
      }
    }
  }, [initializeRecognition, onError]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      isListeningRef.current = false;
      recognitionRef.current.stop();
      setStatus('inactive');
    }
  }, []);

  return {
    isListening: status === 'listening',
    error,
    status,
    startListening,
    stopListening,
    hasRecognitionSupport,
  };
};
